import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';

type Props = {
	isSelected: boolean;
	label: string;
	count: number;
	onClick: () => void;
	selectedCategory: string | undefined;
};

export const Lozenge = ({ label, count, isSelected, onClick, selectedCategory }: Props) => {
	const { formatNumber } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isAllCategories = selectedCategory === undefined;
	return (
		<Button
			role="tab"
			title={label}
			aria-selected={isSelected}
			aria-haspopup="true"
			aria-controls={`related-work-list-${isAllCategories ? 'all-categories' : selectedCategory}`}
			isSelected={isSelected}
			onClick={() => {
				onClick();
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'lozenge',
					}),
					'relatedWorkCategory',
				);
			}}
			data-testid="software-releases-version-related-work.ui.category-filter.lozenge.button"
		>
			<Label>{label}</Label>
			<Count>{formatNumber(count)}</Count>
		</Button>
	);
};

interface ButtonProps {
	title: string;
	isSelected: boolean;
	children: ReactNode;
	['data-testid']: string;
	onClick: () => void;
}

// RLSS-2812 Replace color.background.neutral to color.background.accent.gray.subtlest
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Button = styled.button<ButtonProps>({
	display: 'flex',
	borderRadius: '14px',
	borderStyle: 'none',
	paddingTop: token('space.050'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.100'),
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ isSelected }: ButtonProps) =>
		isSelected
			? token('color.background.selected')
			: // RLSS-2812 Replace color.background.neutral to color.background.accent.gray.subtlest

				token('color.background.neutral'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ isSelected }: ButtonProps) =>
		isSelected ? token('color.text.information') : token('color.text.subtle'),
	font: token('font.body.UNSAFE_small'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '18px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Label = styled.span({
	display: 'inline-block',
	maxWidth: '400px',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Count = styled.span({
	fontWeight: token('font.weight.bold'),
	paddingLeft: token('space.075'),
});
